import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Connection } from '@domain/connection/models/Connection';
import { Connector } from '@domain/connection/models/Connector';
import { ClientType } from '@domain/connection/models/Connector';
import { ProductFlowSettings } from '@domain/connection/models/ConnectionSettings';
import { Resource } from '@domain/base/Resource';
import { InventorySyncEventItem } from '@domain/connection/models/InventorySyncEvent';
import { useFullSync } from '@features/connections/generic/hooks/useFullSync';


export const useProductFlowConnector = () => {
    const { data: connector, isLoading, error } = useQuery<Connector | undefined>({
        queryKey: ['product-flow-connector'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnectors();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.find(connector => connector.type === 'product_flow');
        }
    });

    return {
        connector,
        isLoading,
        error
    };
};

export const useProductFlowConnections = () => {
    const { data: connections, isLoading } = useQuery<Connection[]>({
        queryKey: ['product-flow-connections'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnections();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.filter(connection => connection.connector.type === ClientType.PRODUCT_FLOW);
        }
    });
    return {
        connections: connections || [],
        isLoading,
    };
};

export const useProductFlowSync = () => {

    const { mutateAsync: syncAllOrders } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.productFlowConnectorRepository.syncAllOrders(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        }
    });

    const { mutateAsync: syncNewOrders } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.productFlowConnectorRepository.syncNewOrders(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        }
    });

    const { mutateAsync: syncProducts } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.productFlowConnectorRepository.syncProducts(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        }
    });

    const { mutateAsync: syncInventory } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.productFlowConnectorRepository.syncInventory(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        }
    });

    const { mutateAsync: syncMarketplaceOffers } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.productFlowConnectorRepository.syncMarketplaceOffers(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        }
    });

    const { triggerFullSync, isSyncing } = useFullSync('product_flow');

    const syncFull = (connection: Connection) =>
        triggerFullSync({
            connectionId: connection.id,
            connectionName: connection.name,
            syncFn: (connectionId) => container.productFlowConnectorRepository.syncFull(connectionId),
        });

    return {
        syncAllOrders,
        syncNewOrders,
        syncProducts,
        syncInventory,
        syncMarketplaceOffers,
        syncFull,
        isSyncing,
    };
};

export const useProductFlowMarketplaces = (connectionId: string) => {

    const { data: marketplaces, isLoading, error } = useQuery<{ machine_name: string, connection_id: string, slug: string, name: string }[]>({
        queryKey: ['product-flow-marketplaces', connectionId],
        queryFn: async () => {
            const result = await container.productFlowConnectorRepository.getMarketplaces(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        enabled: !!connectionId
    });

    return {
        marketplaces: marketplaces || [],
        isLoading,
        error
    };
};

export const useProductFlowWarehouseInventory = (connectionId: string) => {
    const { data: warehouseInventory, isLoading, error } = useQuery<InventorySyncEventItem[]>({
        queryKey: ['product-flow-warehouse-inventory', connectionId],
        queryFn: async () => {
            const result = await container.productFlowConnectorRepository.fetchWarehouseInventory(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        enabled: !!connectionId
    });

    return {
        warehouseInventory: warehouseInventory || [],
        isLoading,
        error
    };
};

export const useProductFlowMarketplaceInventory = (connectionId: string, marketplaceSlug: string) => {
    const { data: marketplaceInventory, isLoading, error } = useQuery<InventorySyncEventItem[]>({
        queryKey: ['product-flow-marketplace-inventory', connectionId, marketplaceSlug],
        queryFn: async () => {
            const result = await container.productFlowConnectorRepository.fetchMarketplaceInventory(connectionId, marketplaceSlug);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        enabled: !!connectionId && !!marketplaceSlug
    });

    return {
        marketplaceInventory: marketplaceInventory || [],
        isLoading,
        error
    };
};

export const useProductFlowSettings = (connectionId: string) => {
    const queryClient = useQueryClient();

    const { data: settingsResponse, isLoading } = useQuery<ProductFlowSettings>({
        queryKey: ['product-flow-settings', connectionId],
        queryFn: async () => {
            const result = await container.productFlowConnectorRepository.getSettings(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        enabled: !!connectionId,
    });

    const { mutateAsync: updateSettings, isPending: isUpdating } = useMutation({
        mutationFn: async (settings: Partial<ProductFlowSettings>) => {
            const result = await container.productFlowConnectorRepository.updateSettings(connectionId, settings);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product-flow-settings', connectionId] });
        },
    });

    return {
        settingsResponse,
        isLoading,
        updateSettings,
        isUpdating,
    };
};